import { useState, useCallback, useMemo } from "react";
import { SortDirection, SortState } from "../types/interfaces/sort";
import {
    SortAscIcon,
    SortDescIcon,
} from "@primer/octicons-react";

export interface OrderingDirectionParam {
    value: string;
    direction: SortDirection;
}

export const useSorting = <T extends string>(
    defaultField: T,
    defaultDirection: SortDirection = "desc"
) => {
    const [sortState, setSortState] = useState<SortState<T>>({
        field: defaultField,
        direction: defaultDirection,
    });

    const handleSort = useCallback((field: T) => {
        setSortState((prev) => {
            if (prev.field === field) {
                return {
                    field,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            } else {
                return { field, direction: defaultDirection };
            }
        });
    }, [defaultDirection]);

    const getSortIcon = useCallback((field: T) => {
        if (sortState.field !== field) {
            return <SortDescIcon />;
        }
        return sortState.direction === 'desc' ? <SortDescIcon /> : <SortAscIcon />;
    }, [sortState.field, sortState.direction]);

    const getSortText = useCallback((field: T, baseLabel: string, descendingText: string = '', ascendingText: string = '') => {
        if (sortState.field !== field) {
            return baseLabel;
        }
        if (sortState.direction === 'desc') {
            return descendingText ? `${baseLabel} (${descendingText})` : baseLabel;
        } else {
            return ascendingText ? `${baseLabel} (${ascendingText})` : baseLabel;
        }
    }, [sortState.field, sortState.direction]);

    const orderingParam = useMemo(() => createOrderingParam(
        sortState.field,
        sortState.direction
    ), [sortState.field, sortState.direction]);

    const orderingDirectionParam = useMemo(() =>
        createOrderingDirectionParam(orderingParam),
        [orderingParam]
    );

    return {
        sortState,
        orderingParam,
        orderingDirectionParam,
        handleSort,
        getSortIcon,
        getSortText,
    };
};

// Если в запрос требуется value или -value
export const createOrderingParam = <T extends string>(
    field: T,
    direction: SortDirection
): string => {
    if (!field) return '';
    const prefix = direction === "asc" ? "-" : "";
    return `${prefix}${field}`;
};

// Если в запрос требуется value и -asc или -des
const createOrderingDirectionParam = (ordering: string): OrderingDirectionParam => {
    const isDesc = ordering.startsWith("-");
    const field = isDesc ? ordering.slice(1) : ordering;
    // Кажется странным
    const direction: SortDirection = !isDesc ? "desc" : "asc";

    return {
        value: field,
        direction,
    };
};